/**
 * Shrinks a photograph in the browser, before it is ever uploaded.
 *
 * **Why here and not on the server.** Tonya changes a site photograph once or
 * twice a year, and a native imaging library is a permanent dependency — a
 * licence to answer, platform binaries in the deploy, and a security advisory
 * feed to watch — bought for a twice-yearly operation. Doing it on a canvas
 * costs nothing and is maintained by the browser.
 *
 * Two things fall out of it that matter more than the tidiness:
 *
 * - **The 12 MB original never leaves her laptop.** Uploading from a phone on
 *   studio wifi is the slow path, and this removes it.
 * - **No server memory.** Decoding a 4000×6000 JPEG costs roughly 70 MB of RAM
 *   per image, on a B1 App Service that has 1.75 GB and shares it with another
 *   application.
 *
 * The server's size cap remains, so a direct API call still cannot store a
 * 25 MB file — this makes the normal path cheap, it is not the guard.
 */

/** Wide enough for a full-bleed hero on a large screen; nothing here is printed. */
const MAX_EDGE = 2000

/**
 * JPEG quality. 0.82 is the usual knee — visually indistinguishable from 0.95
 * on photographs, at roughly half the bytes.
 */
const QUALITY = 0.82

/** Below this, resizing is a waste of time and can make the file *larger*. */
const LEAVE_ALONE_BELOW = 400 * 1024

export type ResizeResult = {
  file: File
  originalBytes: number
  bytes: number
  width: number
  height: number
  /** False when the original was already small enough to send as it is. */
  resized: boolean
}

/**
 * Longest edge capped, aspect ratio kept.
 *
 * Never enlarges: a 600px image asked to fill 2000 would be blurred and four
 * times the size, which is worse in both directions.
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { width, height }

  const scale = maxEdge / longest

  // Rounded, and never to zero — a 1px edge is still an image; a 0px one is a
  // canvas that throws.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

/** Whether shrinking this file is worth doing at all. */
export function shouldResize(file: File, floor: number = LEAVE_ALONE_BELOW): boolean {
  // PNGs of the logo and GIFs are left alone: re-encoding a graphic as JPEG
  // loses transparency, and a small file has nothing to gain.
  if (!/^image\/(jpeg|png|webp)$/i.test(file.type)) return false
  if (file.type.toLowerCase() === 'image/png' && file.size < floor) return false

  return file.size >= floor
}

/** "12.4 MB → 284 KB", so the saving is visible rather than claimed. */
export function describeSaving(result: ResizeResult): string {
  const size = (bytes: number) =>
    bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`

  return result.resized
    ? `${size(result.originalBytes)} → ${size(result.bytes)}`
    : size(result.bytes)
}

/**
 * Resizes if it is worth it, and returns the original untouched if not.
 *
 * **Never throws.** A photograph the browser cannot decode is uploaded as it
 * came — the studio's picture reaching the site slightly too large is a far
 * better outcome than an upload that fails with a technical message.
 */
export async function resizeForUpload(file: File): Promise<ResizeResult> {
  const unchanged: ResizeResult = {
    file,
    originalBytes: file.size,
    bytes: file.size,
    width: 0,
    height: 0,
    resized: false,
  }

  if (!shouldResize(file)) return unchanged

  try {
    const bitmap = await createImageBitmap(file)
    const { width, height } = fitWithin(bitmap.width, bitmap.height)

    if (width === bitmap.width && height === bitmap.height) {
      bitmap.close()
      return unchanged
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const context = canvas.getContext('2d')
    if (!context) return unchanged

    context.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    )

    // Re-encoding can grow a file — a flat graphic saved as JPEG is the usual
    // case. Keeping whichever is smaller means this can never make things
    // worse.
    if (!blob || blob.size >= file.size) return unchanged

    return {
      file: new File([blob], renameToJpeg(file.name), { type: 'image/jpeg' }),
      originalBytes: file.size,
      bytes: blob.size,
      width,
      height,
      resized: true,
    }
  } catch {
    return unchanged
  }
}

/**
 * The name follows the format, so a re-encoded PNG does not arrive claiming to
 * be one — the extension is what the storage layer keys off (D-109).
 */
export function renameToJpeg(name: string): string {
  const stem = name.replace(/\.[a-z0-9]+$/i, '')
  return `${stem || 'photo'}.jpg`
}
